import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";
import liberchemLogo from "@/assets/liberchem-logo.png";
import customerCareIcon from "@/assets/customer-care-icon.png";

const ProductLiberchem = () => {
  return (
    <>
      <Helmet>
        <title>Liberchem - Solutions de nettoyage et d'assainissement pour l'industrie laitière</title>
        <meta 
          name="description" 
          content="Liberchem offre des solutions de nettoyage et d'assainissement de qualité supérieure pour maintenir l'hygiène et la qualité de votre production laitière." 
        />
      </Helmet>

      <div className="-mx-4 -mt-16">
        {/* Hero Section */}
        <section 
          className="relative bg-cover bg-center text-white py-32"
          style={{
            backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(https://images.unsplash.com/photo-1532094349884-543bc11b234d?q=80&w=2070)',
            minHeight: '500px'
          }}
        >
          <div className="container mx-auto px-4">
            <div className="max-w-3xl">
              <div className="w-16 h-1 bg-accent mb-6"></div>
              <h1 className="text-3xl md:text-5xl font-bold mb-6">
                Liberchem: Solutions de nettoyage et d'assainissement pour l'industrie laitière
              </h1>
              <p className="text-lg leading-relaxed mb-4">
                Dans l'industrie laitière, la propreté et l'hygiène sont primordiales. Liberchem, un leader reconnu dans le domaine des solutions de nettoyage et d'assainissement, offre des produits de qualité supérieure spécialement conçus pour répondre aux exigences strictes de l'industrie laitière.
              </p>
              <p className="text-lg leading-relaxed">
                Chez Agro, nous sommes fiers de proposer la gamme complète de produits Liberchem. Nous croyons en l'importance de maintenir des normes d'hygiène élevées pour garantir la qualité du lait et la sécurité alimentaire.
              </p>
            </div>
          </div>
        </section>

        {/* Logo Section */}
        <section className="bg-white py-16">
          <div className="container mx-auto px-4 text-center">
            <img 
              src={liberchemLogo} 
              alt="Liberchem Logo"
              className="mx-auto max-w-md h-auto"
            />
          </div>
        </section>

        {/* Pourquoi choisir Liberchem */}
        <section className="bg-gray-50 py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Pourquoi choisir Liberchem?
              </h2>
              <p className="text-lg text-muted-foreground">
                Les avantages d'une hygiène optimale avec Liberchem
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Qualité supérieure */}
              <div className="bg-white p-8 rounded-lg shadow-sm">
                <div className="mb-4">
                  <CheckCircle2 className="w-12 h-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-4">Qualité supérieure</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Les produits Liberchem sont formulés avec les meilleurs ingrédients pour garantir un nettoyage efficace et sûr de vos équipements laitiers. Ils respectent les normes les plus strictes de l'industrie.
                </p>
              </div>

              {/* Efficacité prouvée */}
              <div className="bg-white p-8 rounded-lg shadow-sm">
                <div className="mb-4">
                  <CheckCircle2 className="w-12 h-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-4">Efficacité prouvée</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Liberchem offre des solutions de nettoyage qui éliminent efficacement les résidus de lait, les bactéries et autres contaminants, assurant ainsi une hygiène optimale de vos installations.
                </p>
              </div>

              {/* Respect de l'environnement */}
              <div className="bg-white p-8 rounded-lg shadow-sm">
                <div className="mb-4">
                  <CheckCircle2 className="w-12 h-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-4">Respect de l'environnement</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Conscient de l'importance de la durabilité, Liberchem s'engage à développer des produits respectueux de l'environnement, sans compromettre leur efficacité.
                </p>
              </div>

              {/* Sécurité alimentaire */}
              <div className="bg-white p-8 rounded-lg shadow-sm">
                <div className="mb-4">
                  <CheckCircle2 className="w-12 h-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-4">Sécurité alimentaire</h3>
                <p className="text-muted-foreground leading-relaxed">
                  En utilisant les produits Liberchem, vous contribuez à maintenir des normes élevées de sécurité alimentaire, protégeant ainsi la santé des consommateurs et la réputation de votre exploitation.
                </p>
              </div>

              {/* Support technique */}
              <div className="bg-white p-8 rounded-lg shadow-sm">
                <div className="mb-4">
                  <CheckCircle2 className="w-12 h-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-4">Support technique</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Liberchem et Agro offrent un support technique complet pour vous aider à choisir les bons produits et à les utiliser correctement, maximisant ainsi leur efficacité.
                </p>
              </div>

              {/* En savoir plus */}
              <div className="bg-accent/10 p-8 rounded-lg shadow-sm flex flex-col justify-center items-center text-center">
                <h3 className="text-xl font-bold mb-4">En savoir plus</h3>
                <p className="text-muted-foreground mb-6">
                  Pour en savoir plus, visitez le site web
                </p>
                <Button 
                  asChild 
                  variant="outline" 
                  className="border-accent text-accent hover:bg-accent hover:text-white"
                >
                  <a 
                    href="https://www.liberchem.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    www.liberchem.com
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>


        {/* CTA Final */}
        <section className="relative bg-[#333333] text-white py-16 overflow-hidden">
          <img src={customerCareIcon} alt="" className="absolute right-8 top-1/2 -translate-y-1/2 w-64 h-64 opacity-10 object-contain pointer-events-none" />
          <div className="container mx-auto px-4 text-center relative z-10">
            <p className="text-sm uppercase tracking-wider mb-4">CONTACTEZ-NOUS</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Prêt à Optimiser Votre Production Laitière?
            </h2>
            <p className="text-base md:text-lg mb-2 max-w-4xl mx-auto leading-relaxed">
              Exploitez pleinement le potentiel de votre production laitière avec Agro. Contactez-nous dès aujourd&apos;hui pour découvrir comment
            </p>
            <p className="text-base md:text-lg mb-2 max-w-4xl mx-auto leading-relaxed">
              nos services et produits peuvent aider à améliorer l&apos;efficacité de votre exploitation et garantir la meilleure qualité de lait possible.
            </p>
            <p className="text-base md:text-lg mb-8 max-w-4xl mx-auto leading-relaxed">
              Nous sommes disponibles 24/7 pour vous assister dans vos urgences.
            </p>
            <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-white">
              <Link to="/contact">Contactez-nous dès maintenant!</Link>
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProductLiberchem;
